import { createContext, useContext, ReactNode } from 'react';

export interface ExpandedViewContent {
  type: 'csv-editor' | 'csv-viewer';
  data: {
    threadStoreId: string;
    integrationId: string;
    threadId: string;
    valueToApproveId?: string;
    editable?: boolean;
    title?: string;
    file?: string;
    containsHeader?: boolean;
    filename?: string;
  };
}

export interface ExpandedViewContextType {
  openExpandedView: (content: ExpandedViewContent) => void;
  closeExpandedView: () => void;
  isOpen: boolean;
  content: ExpandedViewContent | null;
}

const ExpandedViewContext = createContext<ExpandedViewContextType | undefined>(undefined);

export function useExpandedView(): ExpandedViewContextType {
  const context = useContext(ExpandedViewContext);

  if (!context) {
    // Return a no-op implementation when used outside a provider
    // This allows components to be used without a provider in contexts where
    // the expanded view functionality is not needed
    return {
      openExpandedView: () => {
        console.warn('useExpandedView: No ExpandedViewProvider found. openExpandedView is a no-op.');
      },
      closeExpandedView: () => {
        console.warn('useExpandedView: No ExpandedViewProvider found. closeExpandedView is a no-op.');
      },
      isOpen: false,
      content: null,
    };
  }

  return context;
}

export { ExpandedViewContext };

interface ExpandedViewProviderProps {
  children: ReactNode;
  value: ExpandedViewContextType;
}

/**
 * Base provider component that can be wrapped by platform-specific implementations.
 * Use WebExpandedViewProvider for main frontend or OutlookExpandedViewProvider for Outlook plugin.
 */
export function ExpandedViewProvider({ children, value }: ExpandedViewProviderProps) {
  return <ExpandedViewContext.Provider value={value}>{children}</ExpandedViewContext.Provider>;
}
